<div class="card">
    <div class="flex justify-between items-center mb-3">
        <div class="text-lg font-semibold">{{ title }}</div>
        <div class="flex align-center gap-2">
            <p-button [outlined]="true"
                      icon="pi pi-filter-slash"
                      label="Clear filters"
                      (onClick)="clearFilters()">
            </p-button>
            @if (enableGlobalSearch) {
                <p-iconfield iconPosition="left">
                    <p-inputicon>
                        <i class="pi pi-search"></i>
                    </p-inputicon>
                    <input pInputText
                           type="text"
                           [(ngModel)]="search"
                           (input)="onGlobalSearch($event)"
                           [placeholder]="globalSearchPlaceholder"
                           class="w-64"/>
                </p-iconfield>
            }
        </div>
    </div>

    <div class="flex justify-between mb-3 items-center">
        <div class="flex items-center gap-4 flex-wrap">
            <span><span class="font-semibold">Total:</span> {{ totalRecords }} items</span>
            @for (column of toolbarFilterColumns; track column.field) {
                <div class="flex items-center gap-2">
                    <span class="text-sm font-medium">{{ column.header }}:</span>
                    @if (column.filterType === FilterType.BOOLEAN) {
                        <p-selectButton [options]="triStateBooleanFilterOptions"
                                        [ngModel]="getToolbarFilterValue(column)"
                                        (ngModelChange)="onToolbarFilterChange(column, $event)"
                                        optionLabel="label"
                                        optionValue="value"
                                        size="small"
                                        class="tristate-boolean-filter toolbar-boolean-filter"/>
                    }
                </div>
            }
        </div>
        @if (createBtnLabel || exportable || toolbarExtraTemplate) {
            <div class="flex justify-end gap-2 mb-3 items-center">
                @if (toolbarExtraTemplate) {
                    <ng-container *ngTemplateOutlet="toolbarExtraTemplate"></ng-container>
                }
                @if (exportable) {
                    <p-button size="small"
                              [outlined]="true"
                              icon="pi pi-download"
                              [loading]="exportLoading"
                              label="Export"
                              (click)="exportData()">
                    </p-button>
                }
                @if (createBtnLabel) {
                    <p-button size="small"
                              icon="pi pi-add"
                              [label]="createBtnLabel"
                              (onClick)="onCreate.emit()">
                    </p-button>
                }
            </div>
        }
    </div>

    <p-table #dataTable
             [value]="data"
             [paginator]="paginator"
             [rows]="rowsPerPage"
             [totalRecords]="totalRecords"
             [lazy]="true"
             [loading]="loading"
             [sortOrder]="defaultSortOrder === 'asc' ? 1 : -1"
             [sortField]="defaultSortField"
             [scrollable]="true"
             [scrollHeight]="resolvedScrollHeight"
             [globalFilterFields]="globalFilterFields"
             [showGridlines]="true"
             [rowsPerPageOptions]="[10, 25, 50]"
             (onLazyLoad)="loadData($event)">
        <ng-template pTemplate="header">
            <tr>
                @for (column of visibleColumns; track column) {
                    @if (column.frozen) {
                        <th pFrozenColumn
                            [alignFrozen]="column.alignFrozen ?? 'left'"
                            [ngClass]="column.headerClass"
                            [class.datatable-header-custom]="!!column.headerClass"
                            [style.min-width.px]="column.minWidth || null"
                            [pSortableColumn]="column.sortable === false ? undefined : column.field">
                            <ng-container *ngTemplateOutlet="headerCell; context: { column: column }"></ng-container>
                        </th>
                    } @else {
                        <th [ngClass]="column.headerClass"
                            [class.datatable-header-custom]="!!column.headerClass"
                            [style.min-width.px]="column.minWidth || null"
                            [pSortableColumn]="column.sortable === false ? undefined : column.field">
                            <ng-container *ngTemplateOutlet="headerCell; context: { column: column }"></ng-container>
                        </th>
                    }
                }
            </tr>
        </ng-template>

        <ng-template #headerCell let-column="column">
            <div class="flex justify-between items-center">
                <div class="flex items-center gap-2">
                    <span>{{ column.header }}</span>
                    @if (column.sortable !== false) {
                        <p-sortIcon [field]="column.field"></p-sortIcon>
                    }
                </div>
                @if (column.filterable !== false && !column.filterInToolbar) {
                    <ng-container *ngTemplateOutlet="filterTemplate; context: { column: column }"></ng-container>
                }
            </div>
        </ng-template>

        <ng-template #filterTemplate let-column="column">
            <p-columnFilter [type]="column.filterType || FilterType.TEXT"
                            [field]="column.field"
                            [matchMode]="filterMatchMode(column)"
                            display="menu"
                            [showMatchModes]="false"
                            [showAddButton]="false"
                            [showOperator]="false"
                            [placeholder]="column.filterPlaceholder || ('Search ' + column.header.toLowerCase())"
                            (onShow)="onFilterOpen(column)">
                @if (column.filterType === FilterType.CUSTOM) {
                    <ng-template #filter let-value let-filter="filterCallback">
                        <ng-container
                            *ngTemplateOutlet="column.filterTemplate; context: { value: value, filter: filter }">
                        </ng-container>
                    </ng-template>
                }

                @if (column.filterType === FilterType.SELECT) {
                    <ng-template #filter let-value let-filter="filterCallback">
                        <p-select [options]="column.filterOptions"
                                  [ngModel]="value"
                                  (onChange)="filter($event.value)"
                                  [filter]="true"
                                  [virtualScroll]="true"
                                  [virtualScrollItemSize]="30"
                                  [showClear]="true"
                                  appendTo="body"
                                  optionLabel="label"
                                  optionValue="value"
                                  placeholder="{{ column.filterPlaceholder || 'Select' }}"
                                  class="w-full">
                        </p-select>
                    </ng-template>
                }

                @if (column.filterType === FilterType.MULTISELECT) {
                    <ng-template #filter let-filterConstraint="filterConstraint">
                        <p-multiSelect [options]="column.filterOptions"
                                       [ngModel]="filterConstraint.value"
                                       (onChange)="filterConstraint.value = $event.value"
                                       optionLabel="label"
                                       optionValue="value"
                                       appendTo="body"
                                       placeholder="{{ column.filterPlaceholder || 'Select options' }}"
                                       class="w-full">
                        </p-multiSelect>
                    </ng-template>
                }

                @if (column.filterType === FilterType.AUTOCOMPLETE) {
                    <ng-template #filter let-filterConstraint="filterConstraint">
                        <p-autoComplete
                            [ngModel]="filterConstraint.value"
                            (ngModelChange)="filterConstraint.value = $event"
                            [suggestions]="column.filterOptions"
                            (completeMethod)="onAutocomplete($event, column)"
                            [completeOnFocus]="true"
                            [forceSelection]="false"
                            [multiple]="true"
                            [fluid]="true"
                            [delay]="500"
                            [scrollHeight]="autocompleteScrollHeight"
                            [panelStyle]="autocompletePanelStyle"
                            [panelStyleClass]="autocompletePanelClass"
                            appendTo="body"
                            class="filter-autocomplete"
                            [placeholder]="column.filterPlaceholder || 'Search'">
                        </p-autoComplete>
                    </ng-template>
                }

                @if (column.filterType === FilterType.BOOLEAN) {
                    <ng-template #filter let-filterConstraint="filterConstraint">
                        <p-selectButton [options]="triStateBooleanFilterOptions"
                                        [ngModel]="filterConstraint.value ?? null"
                                        (ngModelChange)="filterConstraint.value = $event"
                                        optionLabel="label"
                                        optionValue="value"
                                        size="small"
                                        class="tristate-boolean-filter w-full"/>
                    </ng-template>
                }

                @if (column.filterType === FilterType.NUMERIC || column.filterType === FilterType.DATE) {
                    <ng-template #filter let-filterConstraint="filterConstraint">
                        <div class="range-filter flex flex-col gap-2">
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium">From</label>
                                @if (column.filterType === FilterType.DATE) {
                                    <p-datepicker [ngModel]="getRangeFilterDatePart(filterConstraint, 'from')"
                                                  (ngModelChange)="setRangeFilterDatePart(filterConstraint, 'from', $event)"
                                                  [dateFormat]="dateFilterFormat"
                                                  [placeholder]="dateFilterPlaceholder"
                                                  [showIcon]="true"
                                                  [showClear]="true"
                                                  appendTo="body"
                                                  class="range-filter-datepicker w-full"/>
                                } @else {
                                    <input pInputText
                                           type="number"
                                           [ngModel]="getRangeFilterPart(filterConstraint, 'from')"
                                           (ngModelChange)="setRangeFilterPart(filterConstraint, 'from', $event)"
                                           [placeholder]="column.filterPlaceholder || 'From'"
                                           class="w-full"/>
                                }
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-medium">To</label>
                                @if (column.filterType === FilterType.DATE) {
                                    <p-datepicker [ngModel]="getRangeFilterDatePart(filterConstraint, 'to')"
                                                  (ngModelChange)="setRangeFilterDatePart(filterConstraint, 'to', $event)"
                                                  [dateFormat]="dateFilterFormat"
                                                  [placeholder]="dateFilterPlaceholder"
                                                  [showIcon]="true"
                                                  [showClear]="true"
                                                  appendTo="body"
                                                  class="range-filter-datepicker w-full"/>
                                } @else {
                                    <input pInputText
                                           type="number"
                                           [ngModel]="getRangeFilterPart(filterConstraint, 'to')"
                                           (ngModelChange)="setRangeFilterPart(filterConstraint, 'to', $event)"
                                           placeholder="To"
                                           class="w-full"/>
                                }
                            </div>
                        </div>
                    </ng-template>
                }
            </p-columnFilter>
        </ng-template>

        <ng-template pTemplate="body" let-row>
            <tr>
                @for (column of visibleColumns; track column) {
                    @if (column.frozen) {
                        <td [ngClass]="column.cellClass"
                            pFrozenColumn
                            [alignFrozen]="column.alignFrozen ?? 'left'">
                            <ng-container
                                *ngTemplateOutlet="bodyCell; context: { column: column, row: row }"></ng-container>
                        </td>
                    } @else {
                        <td [ngClass]="column.cellClass">
                            <ng-container
                                *ngTemplateOutlet="bodyCell; context: { column: column, row: row }"></ng-container>
                        </td>
                    }
                }
            </tr>
        </ng-template>

        <ng-template #bodyCell let-column="column" let-row="row">
            @if (column.template) {
                <ng-container
                    [ngTemplateOutlet]="column.template"
                    [ngTemplateOutletContext]="{
                row: row,
                column: column,
                value: renderCell(row, column)
            }">
                </ng-container>
            } @else {
                @if (column.editable && isEditing(row, column)) {
                    <div class="flex items-center gap-2 w-full">
                        @switch (column.editType) {
                            @case (FilterType.TEXT) {
                                <input pInputText
                                       [(ngModel)]="editingCell!.value"
                                       class="w-full"/>
                            }
                            @case (FilterType.NUMERIC) {
                                <input pInputText
                                       type="number"
                                       [(ngModel)]="editingCell!.value"
                                       class="w-full"/>
                            }
                            @case (FilterType.SELECT) {
                                <p-select [options]="column.editOptions"
                                          [(ngModel)]="editingCell!.value"
                                          [showClear]="true"
                                          optionLabel="label"
                                          optionValue="value"
                                          placeholder="Select"
                                          class="w-full">
                                </p-select>
                            }
                            @case (FilterType.MULTISELECT) {
                                <p-multiSelect [options]="column.editOptions"
                                               [(ngModel)]="editingCell!.value"
                                               optionLabel="label"
                                               optionValue="value"
                                               class="w-full">
                                </p-multiSelect>
                            }
                            @case (FilterType.AUTOCOMPLETE) {
                                <p-autoComplete [(ngModel)]="editingCell!.value"
                                                [suggestions]="column.editAutocompleteOptions"
                                                (completeMethod)="onEditableAutocomplete($event, column)"
                                                (onSelect)="column.editAutocompleteSelectMethod?.(row, $event)"
                                                [completeOnFocus]="true"
                                                [forceSelection]="false"
                                                [showClear]="true"
                                                [delay]="500"
                                                [scrollHeight]="autocompleteScrollHeight"
                                                [panelStyle]="autocompletePanelStyle"
                                                [panelStyleClass]="autocompletePanelClass"
                                                optionLabel="label"
                                                optionValue="value"
                                                placeholder="Search..."
                                                appendTo="body"
                                                class="editable-autocomplete">
                                </p-autoComplete>
                            }
                            @case (FilterType.BOOLEAN) {
                                <p-checkbox
                                    [binary]="true"
                                    [(ngModel)]="editingCell!.value">
                                </p-checkbox>
                            }
                        }
                        <p-button icon="pi pi-check"
                                  size="small"
                                  severity="secondary"
                                  [outlined]="true"
                                  (onClick)="saveEdit(column)">
                        </p-button>
                        <p-button icon="pi pi-times"
                                  size="small"
                                  severity="secondary"
                                  [outlined]="true"
                                  (onClick)="cancelEdit()">
                        </p-button>
                    </div>

                } @else {
                    @switch (column.type) {
                        @case (DataTableColumnType.DATE) {
                            <span class="text-nowrap">{{ renderCell(row, column) | date:'MM/dd/yyyy' | valueOr }}</span>
                        }
                        @case (DataTableColumnType.DATETIME) {
                            <span class="text-nowrap">{{ renderCell(row, column) | date:'short' | valueOr }}</span>
                        }
                        @case (DataTableColumnType.NUMBER) {
                            <span>{{ renderCell(row, column) | number | valueOr }}</span>
                        }
                        @case (DataTableColumnType.BOOLEAN) {
                            <span>{{ renderCell(row, column) ? 'Yes' : 'No' }}</span>
                        }
                        @case (DataTableColumnType.EMAIL) {
                            <a [href]="'mailto:' + renderCell(row, column)">
                                {{ renderCell(row, column) | valueOr }}
                            </a>
                        }
                        @case (DataTableColumnType.PHONE) {
                            @if (renderCell(row, column)) {
                                <a href="tel:{{ renderCell(row, column) | phone }}" class="text-nowrap">
                                    {{ renderCell(row, column) | phone }}
                                </a>
                            } @else {
                                <span>{{ renderCell(row, column) | valueOr }}</span>
                            }
                        }
                        @case (DataTableColumnType.CODE) {
                            <code>{{ renderCell(row, column) | valueOr }}</code>
                        }
                        @case (DataTableColumnType.PERCENT) {
                            <span>{{ renderCell(row, column) | percent:'0.0-2' | valueOr }}</span>
                        }
                        @case (DataTableColumnType.ARRAY) {
                            {{ renderCell(row, column) | join:', ' | valueOr }}
                        }
                        @case (DataTableColumnType.JSON) {
                            <pre>{{ renderCell(row, column) | json | valueOr }}</pre>
                        }
                        @case (DataTableColumnType.LINK) {
                            @if (column.link && column.link(row)) {
                                <a [href]="column.link ? column.link(row) : renderCell(row, column)" target="_blank">
                                    {{ renderCell(row, column) }}
                                </a>
                            } @else {
                                <span>{{ renderCell(row, column) | valueOr }}</span>
                            }
                        }
                        @case (DataTableColumnType.TAG) {
                            <p-tag>{{ renderCell(row, column) | valueOr }}</p-tag>
                        }
                        @case (DataTableColumnType.ROLE) {
                            <app-role [value]="renderCell(row, column)"></app-role>
                        }
                        @case (DataTableColumnType.AVATAR) {
                            <app-avatar [name]="renderCell(row, column)"></app-avatar>
                        }
                        @case (DataTableColumnType.ICON) {
                            <i class="pi" [ngClass]="renderCell(row, column)"></i>
                        }
                        @case (DataTableColumnType.MODAL) {
                            <div class="flex items-center gap-2">
                                <div class="ellipsis"
                                     [pTooltip]="renderCell(row, column)">{{ renderCell(row, column) }}
                                </div>
                                <p-button icon="pi pi-eye"
                                          [outlined]="true"
                                          size="small"
                                          [pTooltip]="column.modalBtnDisabled && column.modalBtnDisabled(row) ? 'Preview is unable for this item' : undefined"
                                          [disabled]="column.modalBtnDisabled ? column.modalBtnDisabled(row) : false"
                                          (onClick)="openModal(column, row)">
                                </p-button>
                            </div>
                        }
                        @case (DataTableColumnType.STATUS) {
                            <app-status [value]="renderCell(row, column)"></app-status>
                        }
                        @case (DataTableColumnType.ELLIPSIS) {
                            <div [pTooltip]="getEllipsisTooltip(row, column)"
                                 class="ellipsis">
                                {{ renderCell(row, column) | valueOr }}
                            </div>
                        }
                        @case (DataTableColumnType.WRAP) {
                            <div [pTooltip]="renderCell(row, column)"
                                 class="wrap-clamp-3">
                                {{ renderCell(row, column) | valueOr }}
                            </div>
                        }
                        @case (DataTableColumnType.CHECKBOX) {
                            <p-checkbox [binary]="true"
                                        [ngModel]="renderCell(row, column)"
                                        (onChange)="column.onCheckboxChange?.($event.checked, row)">
                            </p-checkbox>
                        }
                        @case (DataTableColumnType.ACTIONS) {
                            <div class="flex gap-2 justify-end">
                                @if (getActions(column, row).length) {
                                    @for (action of getActions(column, row); track action.label) {
                                        <p-button [icon]="action.icon"
                                                  [label]="action.label"
                                                  [outlined]="true"
                                                  size="small"
                                                  (onClick)="action.command(row)">
                                        </p-button>
                                    }
                                } @else {
                                    <span class="text-nowrap text-center text-sm text-gray-400 w-full">No actions</span>
                                }
                            </div>
                        }
                        @default {
                            <span>{{ renderCell(row, column) | valueOr }}</span>
                        }
                    }
                    @if (column.editable) {
                        <p-button icon="pi pi-pencil"
                                  size="small"
                                  [text]="true"
                                  (onClick)="startEdit(row, column)">
                        </p-button>
                    }
                }
            }
        </ng-template>

        <ng-template pTemplate="emptymessage">
            <tr>
                <td [attr.colspan]="visibleColumns.length" class="text-center">No data found.</td>
            </tr>
        </ng-template>
    </p-table>
</div>
